// ============================================================
//  src/pages/auth/ForgotPasswordPage.js
//  Three steps in one page:
//    1) request — enter email, get OTP
//    2) verify  — enter OTP, get reset_token
//    3) reset   — enter new password
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { forgotPasswordReq, forgotPasswordVerify, forgotPasswordReset } from '../../api/client';
import { getError } from '../../utils/helpers';
import './auth.css';

export default function ForgotPasswordPage() {
  const nav = useNavigate();
  const [step, setStep]         = useState(1);
  const [email, setEmail]       = useState('');
  const [digits, setDigits]     = useState(['', '', '', '', '', '']);
  const [resetToken, setRT]     = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const inputs = useRef([]);

  useEffect(() => {
    if (step === 2) inputs.current[0]?.focus();
  }, [step]);

  // ── OTP grid helpers ──
  const onDigit = (idx, val) => {
    const v = val.replace(/\D/g, '').slice(0, 1);
    setDigits((p) => { const n = [...p]; n[idx] = v; return n; });
    if (v && idx < 5) inputs.current[idx + 1]?.focus();
  };
  const onKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) inputs.current[idx - 1]?.focus();
  };
  const onPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    inputs.current[Math.min(text.length, 5)]?.focus();
  };

  // ── Step 1: request OTP ──
  const submitRequest = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await forgotPasswordReq({ email: email.trim() });
      if (!data?.success) { setError(data?.message || 'Could not send code.'); return; }
      toast.success(data?.message || 'Code sent.');
      setStep(2);
    } catch (err) {
      setError(getError(err));
    } finally { setLoading(false); }
  };

  // ── Step 2: verify OTP ──
  const submitVerify = async (e) => {
    e.preventDefault();
    const otp = digits.join('');
    if (otp.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await forgotPasswordVerify({ email: email.trim(), otp });
      if (!data?.success) { setError(data?.message || 'Invalid code.'); return; }
      setRT(data?.data?.reset_token || '');
      setStep(3);
    } catch (err) {
      setError(getError(err));
    } finally { setLoading(false); }
  };

  // ── Step 3: reset password ──
  const submitReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await forgotPasswordReset({
        email: email.trim(),
        reset_token: resetToken,
        password,
      });
      if (!data?.success) { setError(data?.message || 'Could not reset password.'); return; }
      toast.success('Password updated.');
      nav('/login', { replace: true });
    } catch (err) {
      setError(getError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-main">
          <div className="auth-brand">
            <span className="auth-brand-dot">C</span>
            <span>CommandKit</span>
          </div>
          <div>
            <div className="auth-headline">No worries.<br /><em>We'll get you back in.</em></div>
            <p className="auth-sub">
              Enter your email and we'll send a code. Verify the code, set a
              new password, and you're back to building bots.
            </p>
          </div>
          <div className="auth-footer-badge">
            <i className="fas fa-lock" /> Reset codes expire in 15 minutes
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-shell">
          <div className="auth-mobile-brand">
            <span className="auth-brand-dot">C</span> CommandKit
          </div>

          <div className="auth-hdr">
            <h2>
              {step === 1 && 'Reset your password'}
              {step === 2 && 'Enter the code'}
              {step === 3 && 'Set a new password'}
            </h2>
            <p>
              {step === 1 && "Enter your account email and we'll send a 6-digit code."}
              {step === 2 && `We sent a code to ${email}.`}
              {step === 3 && 'Pick something strong, between 6 and 128 characters.'}
            </p>
          </div>

          {error && (
            <div className="auth-alert auth-alert-error">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={submitRequest} noValidate>
              <div className="auth-field">
                <label>Email</label>
                <div className="auth-field-wrap">
                  <i className="fas fa-envelope fi" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn-auth" disabled={loading}>
                {loading
                  ? <><span className="spinner" /> Sending…</>
                  : <><i className="fas fa-paper-plane" /> Send code</>
                }
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitVerify} noValidate>
              <label className="form-label">Verification code</label>
              <div className="otp-grid" onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputs.current[i] = el)}
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => onDigit(i, e.target.value)}
                    onKeyDown={(e) => onKeyDown(i, e)}
                  />
                ))}
              </div>
              <button type="submit" className="btn-auth" disabled={loading}>
                {loading
                  ? <><span className="spinner" /> Verifying…</>
                  : <><i className="fas fa-check" /> Verify code</>
                }
              </button>
              <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13.5 }}>
                <button type="button" onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  ← Use a different email
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={submitReset} noValidate>
              <div className="auth-field">
                <label>New password</label>
                <div className="auth-field-wrap">
                  <i className="fas fa-lock fi" />
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                    required
                  />
                  <button type="button" className="pw-toggle" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}>
                    <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>
              <button type="submit" className="btn-auth" disabled={loading}>
                {loading
                  ? <><span className="spinner" /> Updating…</>
                  : <><i className="fas fa-check" /> Update password</>
                }
              </button>
            </form>
          )}

          <div className="auth-bottom">
            Remembered it? <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}